import { useEffect, useRef } from "react";
import {
  isInboxRealtimeDelivering,
  REALTIME_FALLBACK_POLL_MS,
  subscribeHospitalInbox,
} from "../realtime/realtimeClient";
import { REALTIME_POLLING_ENABLED, REALTIME_REST_SYNC_ENABLED } from "../realtime/realtimeConfig";
import { REALTIME_EVENT, RealtimeEventPayload } from "../realtime/realtimeTypes";

type Options = {
  enabled?: boolean;
  onInboxEvent?: (event: RealtimeEventPayload) => void;
  onFallbackPoll?: () => void;
  /** Defaults to all hospital inbox signals; pass a subset to avoid unrelated refetches. */
  eventTypes?: ReadonlySet<string>;
};

export const HOSPITAL_INBOX_EVENT_TYPES = new Set<string>([
  REALTIME_EVENT.CONVERSATION_MESSAGE_CREATED,
  REALTIME_EVENT.CONVERSATION_UNREAD_UPDATED,
  REALTIME_EVENT.NOTIFICATION_CREATED,
  REALTIME_EVENT.NOTIFICATION_UNREAD_COUNT,
  REALTIME_EVENT.FEED_POST_CREATED,
  REALTIME_EVENT.FEED_POST_UPDATED,
  REALTIME_EVENT.FEED_POST_COMMENTED,
  REALTIME_EVENT.FEED_POST_LIKED,
  REALTIME_EVENT.STORY_RING_UPDATED,
]);

/**
 * Keeps hospital inbox, bell notifications, and social signals fresh via WebSocket.
 * Callbacks are stored in refs so subscriptions are not torn down on every render.
 * Fallback polling runs only while the WebSocket is disconnected.
 */
export function useHospitalRealtime({
  enabled = true,
  onInboxEvent,
  onFallbackPoll,
  eventTypes = HOSPITAL_INBOX_EVENT_TYPES,
}: Options) {
  const onInboxRef = useRef(onInboxEvent);
  const onFallbackRef = useRef(onFallbackPoll);
  const eventTypesRef = useRef(eventTypes);
  onInboxRef.current = onInboxEvent;
  onFallbackRef.current = onFallbackPoll;
  eventTypesRef.current = eventTypes;

  useEffect(() => {
    if (!enabled) return;

    const handleEvent = (event: RealtimeEventPayload) => {
      const type = String(event.type || "");
      if (!eventTypesRef.current.has(type)) return;
      if (REALTIME_REST_SYNC_ENABLED) {
        onInboxRef.current?.(event);
      }
    };

    const unsubscribe = subscribeHospitalInbox(handleEvent);

    if (!REALTIME_POLLING_ENABLED) {
      return () => {
        unsubscribe();
      };
    }

    const fallbackTimer = window.setInterval(() => {
      if (isInboxRealtimeDelivering()) return;
      onFallbackRef.current?.();
    }, REALTIME_FALLBACK_POLL_MS);

    return () => {
      unsubscribe();
      window.clearInterval(fallbackTimer);
    };
  }, [enabled]);
}
